import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateUtils';
import { getWeekdayLabel, type I18nLike } from '@/types/changeRequest';
import type { change_requests_CareDayPlanResponse } from '@/api';

interface CareDayBillingDeltaProps {
  plan: change_requests_CareDayPlanResponse;
  i18n: I18nLike;
}

function DeltaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// weekdaysLabel renders an ISO-weekday list (e.g. [1,2,4]) as localized names.
function weekdaysLabel(i18n: I18nLike, days: number[] | undefined): string {
  if (!days || days.length === 0) return '—';
  return days.map((d) => getWeekdayLabel(i18n, d)).join(', ');
}

/**
 * CareDayBillingDelta renders the read-only billing-delta preview for a bounded
 * care_day change (#783): the [active_from, active_to] window, the add/remove
 * weekday op, the current → proposed weekdays, and the invoices the window will
 * affect. Read-only summary in semantic markup (no raw <table>); status is
 * conveyed by text, not colour alone. Mirrors the termination affected-invoice
 * block.
 */
export function CareDayBillingDelta({ plan, i18n }: CareDayBillingDeltaProps) {
  const isRemove = plan.sub_type === 'remove_day';
  const weekdayName = typeof plan.weekday === 'number' ? getWeekdayLabel(i18n, plan.weekday) : '';
  const affected = plan.affected_invoices ?? [];

  const actionLabel = (action: string | undefined): string => {
    switch (action) {
      case 'cancel':
        return i18n._({ id: 'careDay.plan.action.cancel', message: 'Cancel' });
      case 'reissue':
        return i18n._({ id: 'careDay.plan.action.reissue', message: 'Re-issue' });
      default:
        return action ?? '';
    }
  };

  return (
    <div className="space-y-4 rounded-md border bg-muted/30 p-3">
      <DeltaRow label={i18n._({ id: 'careDay.plan.window', message: 'Trial window' })}>
        <p className="text-sm">
          {formatDate(plan.active_from)} – {formatDate(plan.active_to)}
        </p>
      </DeltaRow>

      <DeltaRow
        label={
          isRemove
            ? i18n._({ id: 'careDay.plan.removeDay', message: 'Remove weekday' })
            : i18n._({ id: 'careDay.plan.addDay', message: 'Add weekday' })
        }
      >
        <p className="text-sm">{weekdayName}</p>
      </DeltaRow>

      <div className="flex flex-wrap gap-6">
        <DeltaRow label={i18n._({ id: 'careDay.plan.currentDays', message: 'Current days' })}>
          <p className="text-sm">{weekdaysLabel(i18n, plan.current_weekdays)}</p>
        </DeltaRow>
        <DeltaRow label={i18n._({ id: 'careDay.plan.proposedDays', message: 'Proposed days' })}>
          <p className="text-sm">{weekdaysLabel(i18n, plan.proposed_weekdays)}</p>
        </DeltaRow>
      </div>

      {plan.no_change ? (
        <p className="text-sm text-warning-on-surface">
          {i18n._({
            id: 'careDay.plan.noChange',
            message: 'No billing change — the weekday is already as requested.',
          })}
        </p>
      ) : (
        <DeltaRow label={i18n._({ id: 'careDay.plan.affectedInvoices', message: 'Affected invoices' })}>
          {affected.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {i18n._({ id: 'careDay.plan.noAffectedInvoices', message: 'No existing invoices affected; the period will be (re)generated.' })}
            </p>
          ) : (
            <ul className="text-sm space-y-1">
              {affected.map((inv, idx) => (
                <li key={`${inv.invoice_id ?? 'inv'}-${idx}`} className="flex items-center gap-2">
                  <span className="font-medium">
                    {inv.period_year}-{String(inv.period_month ?? 0).padStart(2, '0')}
                  </span>
                  <Badge variant="outline">{inv.status}</Badge>
                  <Badge variant={inv.action === 'cancel' ? 'destructive' : 'secondary'}>
                    {actionLabel(inv.action)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </DeltaRow>
      )}
    </div>
  );
}
